/**
 * @author Andrés Camilo Ferreira Rodríguez
 * @Copyright (c) Infotrack. All rights reserved.
 */
import { useProveedor } from '@infotrack/presentacion-componentes/proveedorEstado';
import Texto from '@infotrack/presentacion-componentes/texto';
import { TipoFormulario } from '@infotrack/presentacion-transversales/interfacesComunes';
import { useInputState } from '@infotrack/presentacion-utilitarios/hooks';
import {
    manejadorDialogoGlobal,
    manejadorIndicadorCargaGlobal,
} from '@infotrack/presentacion-utilitarios/manejadoresComponentes';
import ITiposUnidades from 'Infotrack@Modelos/smartStock/tiposUnidades/entidades/tiposUnidades';
import IUnidades from 'Infotrack@Modelos/smartStock/unidades/entidades/unidades';
import { IFormularioUnidadesCVProps } from 'Infotrack@Modulos/inventario/gestionProducto/interfaces';
import { IEstadoGlobal } from 'Infotrack@Reductores/interfacesReductores';
import React from 'react';
import { connect } from 'react-redux';
import TiposUnidadesCM from '../../tiposUnidades/controladorModelo/tiposUnidadesCM';
import UnidadesCM from '../controladorModelo/unidadesCM';
import { IInputsUnidades } from '../interfaces';
import { acciones, IEstadoUnidades } from '../reductorUnidades';
import FormularioUnidadesVista from '../vista/formularioUnidadesVista';
import { useSelector } from 'react-redux';
import { estadoActivo } from 'Infotrack@Transversales/constantes/ConstantesEstados';

const estadoInicial: IInputsUnidades = {
    EmpresaId: '',
    TipoUnidad: null,
    DescripcionUnidad: '',
    Abreviatura: '',
    Factor: '',
    Estado: true
};
const unidadesCM = new UnidadesCM();
const tiposUnidadesCM = new TiposUnidadesCM();

const FormularioUnidadesCV: React.FunctionComponent<IFormularioUnidadesCVProps> = ({ IdEmpresa }) => {
    const idAgencia = useSelector((estado: IEstadoGlobal) => estado.estadoAutenticacion.agenciaActual!.IdAgencia);
    const [{ estadoFormulario, tipoFormulario, entidadUnidades }, dispatch] = useProveedor<IEstadoUnidades>();
    const { alCambiarValor, setValor, reiniciar, valor } = useInputState<IInputsUnidades>(estadoInicial);

    const [tiposUnidades, setTiposUnidades] = React.useState<ITiposUnidades[]>([]);
    const idEmpresa = useSelector((e: IEstadoGlobal) => e.estadoAutenticacion.usuarioInformacion!.IdEmpresa);

    
    React.useEffect(() => {
        cargarTiposUnidades();
        if (entidadUnidades && tipoFormulario !== TipoFormulario.Creacion) {
            validarDatosInactivos();
            setValor(entidadUnidades);
        } else {
            reiniciar();
        }
    }, [entidadUnidades]);

    const cerrarFormulario = () => {
        dispatch({ type: acciones.CAMBIAR_ESTADO_FORMULARIO, payload: null });
    };

    const cargarTiposUnidades = async () => {
        setTiposUnidades(await tiposUnidadesCM.consultarTiposUnidadesPorFiltro({ EmpresaId: idEmpresa,Estado: estadoActivo }));
    };

    const confirmarCreacionEdicionUnidades = () => {
        if (unidadesCM.validarEntidad(mapearDatos())) {
            manejadorDialogoGlobal({
                accionCancelar: () => manejadorDialogoGlobal({ estado: false }),
                accionConfirmar: crearEditarUnidades,
                estado: true,
                mensaje: (
                    <Texto
                        id={
                            tipoFormulario === TipoFormulario.Creacion
                                ? 'alerta.confirmacionCreacion'
                                : 'alerta.confirmacionEdicion'
                        }
                    />
                ),
                mostrarCancelar: true,
                tipoDialogo: 'Advertencia',
                titulo: <Texto id="titulo.advertencia" />,
            });
        }
    };

    const crearEditarUnidades = () => {
        manejadorDialogoGlobal({ estado: false });
        manejadorIndicadorCargaGlobal(true);
        if (tipoFormulario === TipoFormulario.Creacion) {
            unidadesCM.crearUnidades(mapearDatos()).then(() => {
                cerrarFormulario();
                manejadorIndicadorCargaGlobal(false);
                dispatch({ type: acciones.RECARGAR_TABLA });
            });
        } else {
            unidadesCM.editarUnidades(mapearDatos()).then(() => {
                cerrarFormulario();
                manejadorIndicadorCargaGlobal(false);
                dispatch({ type: acciones.RECARGAR_TABLA });
            });
        }
    };

    const validarDatosInactivos = () => {
        if (entidadUnidades && !tiposUnidades.find((x) => entidadUnidades.TipoUnidad && x.TipoUnidadId === entidadUnidades.TipoUnidad!.TipoUnidadId)) {
            entidadUnidades.TipoUnidad = null;
            setValor(entidadUnidades);   
        }   
    };

    const alCambiarValorAutoComplete = (tipoUnidad: ITiposUnidades) => {
        setValor({ ...valor, TipoUnidad: tipoUnidad });
    };

    const mapearDatos = (): IUnidades => ({
        ...valor,
        EmpresaId: IdEmpresa,
        Factor: Number(valor.Factor),
        TipoUnidadId: Number(valor.TipoUnidad ? valor.TipoUnidad.TipoUnidadId : 0),
        Estado: valor.Estado ? 1 : 0,
        AgenciaId: idAgencia,
    });

    return (
        <FormularioUnidadesVista
            accionCrearEditar={confirmarCreacionEdicionUnidades}
            alCambiarValor={alCambiarValor}
            alCambiarValorAutoComplete={alCambiarValorAutoComplete}
            cerrarFormulario={cerrarFormulario}
            estadoFormulario={estadoFormulario}
            tipoFormulario={tipoFormulario}
            tiposUnidades={tiposUnidades}
            unidades={valor}
        />
    );
};

const estadoAPropiedades = ({
    estadoAutenticacion: { usuarioInformacion },
}: IEstadoGlobal): IFormularioUnidadesCVProps => ({
    IdEmpresa: usuarioInformacion!.IdEmpresa,
});

export default connect<IFormularioUnidadesCVProps, null, any, IEstadoGlobal>(
    estadoAPropiedades,
    null
)(FormularioUnidadesCV);
